import { type ReactNode } from 'react';
import createCache from '@emotion/cache';
import { CacheProvider, Global, css } from '@emotion/react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import * as WebFont from 'webfontloader';
import theme from '@config/theme/theme';
import gazpachoBlackUrl from '@assets/fonts/Gazpacho-Black.otf';
import agrandirNarrowMediumUrl from '@assets/fonts/Agrandir Narrow Medium.ttf';

let fontsLoaded = false;
const loadFonts = () => {
  if (fontsLoaded) return;
  fontsLoaded = true;
  WebFont.load({
    google: {
      families: ['Roboto:300,400,500,600,700,800,900', 'Montserrat:300,400,500,600,700,800,900', 'Inter:700'],
    },
  });

  // Register local fonts at document level so they work inside Shadow DOM.
  // Guard for environments without the FontFace API (jsdom in tests, SSR).
  if (typeof FontFace === 'undefined' || !document.fonts) return;

  const gazpachoBlack = new FontFace('Gazpacho Black', `url(${gazpachoBlackUrl})`, {
    weight: '900',
    style: 'normal',
    display: 'swap',
  });
  gazpachoBlack.load().then((loaded) => document.fonts.add(loaded));

  const agrandir = new FontFace('Agrandir', `url(${agrandirNarrowMediumUrl})`, {
    weight: '500',
    style: 'normal',
    display: 'swap',
  });
  agrandir.load().then((loaded) => document.fonts.add(loaded));
};

// Shadow DOM variant: portals disabled so all MUI overlays render inside the shadow root
const shadowDomTheme = createTheme(theme, {
  components: {
    MuiDialog: { defaultProps: { disablePortal: true } },
    MuiModal: { defaultProps: { disablePortal: true } },
    MuiPopover: { defaultProps: { disablePortal: true } },
    MuiPopper: { defaultProps: { disablePortal: true } },
    MuiTooltip: { defaultProps: { disablePortal: true } },
  },
});

type ThemeWrapperProps = {
  children: ReactNode;
  emotionContainer?: ShadowRoot;
};

/**
 * Provides MUI theme + Emotion cache for the application.
 * When emotionContainer is set (web component mode), styles inject into the
 * shadow root and MUI portals are disabled so overlays stay within the shadow boundary.
 */
const ThemeWrapper = ({ children, emotionContainer }: ThemeWrapperProps) => {
  loadFonts();

  const cache = createCache({
    key: 'cpq',
    prepend: true,
    ...(emotionContainer !== undefined ? { container: emotionContainer } : {}),
  });

  const activeTheme = emotionContainer !== undefined ? shadowDomTheme : theme;

  return (
    <CacheProvider value={cache}>
      {/*
       * Scoped reset: every selector is wrapped in :where(.cpq-root) so styles
       * cannot leak into the host document. :where() carries zero specificity,
       * so host rules on the same elements always win. html/body rules are
       * omitted — a library must not own document-level selectors.
       */}
      <Global
        styles={css`
          :where(.cpq-root),
          :where(.cpq-root) *,
          :where(.cpq-root) *::before,
          :where(.cpq-root) *::after {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }

          :where(.cpq-root) {
            font-family: 'Roboto', sans-serif;
            line-height: 1.15;
          }

          :where(.cpq-root) img,
          :where(.cpq-root) picture,
          :where(.cpq-root) video,
          :where(.cpq-root) svg {
            display: block;
            max-width: 100%;
          }

          :where(.cpq-root) input,
          :where(.cpq-root) button,
          :where(.cpq-root) textarea,
          :where(.cpq-root) select {
            font: inherit;
          }

          :where(.cpq-root) a {
            color: inherit;
            text-decoration: none;
          }

          :where(.cpq-root) ul,
          :where(.cpq-root) ol {
            list-style: none;
          }

          :where(.cpq-root) table {
            border-collapse: collapse;
            border-spacing: 0;
          }

          :where(.cpq-root) h1,
          :where(.cpq-root) h2,
          :where(.cpq-root) h3,
          :where(.cpq-root) h4,
          :where(.cpq-root) h5,
          :where(.cpq-root) h6 {
            font-size: inherit;
            font-weight: inherit;
          }
        `}
      />
      <ThemeProvider theme={activeTheme}>
        <div className="cpq-root">{children}</div>
      </ThemeProvider>
    </CacheProvider>
  );
};

export default ThemeWrapper;
